import { Raffle, RaffleStatus } from '@/types';
import { Users, Clock, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import clsx from 'clsx';

interface RaffleCardProps {
  raffle: Raffle;
  showWinner?: boolean;
}

export const RaffleCard = ({ raffle, showWinner = false }: RaffleCardProps) => {
  const navigate = useNavigate();
  const [modalImage, setModalImage] = useState<string | null>(null);

  const getStatusColor = (status: RaffleStatus) => {
    switch (status) {
      case RaffleStatus.ACTIVE:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case RaffleStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case RaffleStatus.COMPLETED:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: RaffleStatus) => {
    switch (status) {
      case RaffleStatus.ACTIVE:
        return 'Активен';
      case RaffleStatus.PENDING:
        return 'Ожидает';
      case RaffleStatus.DRAWING:
        return 'Розыгрыш';
      case RaffleStatus.COMPLETED:
        return 'Завершен';
      default:
        return status;
    }
  };

  return (
    <div
      onClick={() => navigate(`/raffle/${raffle.id}`)}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
    >
      {/* Photo */}
      {raffle.photo_url && (
        <div
          className="w-full h-48 overflow-hidden cursor-zoom-in"
          onClick={(e) => {
            e.stopPropagation();
            setModalImage(raffle.photo_url!);
          }}
        >
          <img
            src={raffle.photo_url}
            alt={raffle.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform"
          />
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {raffle.title}
            </h3>
            {raffle.prizes && raffle.prizes.length > 0 && (
              <div className="mt-2 space-y-1">
                {raffle.prizes.slice(0, 2).map((prize) => (
                  <p key={prize.place} className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                    🎁 {prize.place} место: {prize.name}
                  </p>
                ))}
                {raffle.prizes.length > 2 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    +{raffle.prizes.length - 2} ещё...
                  </p>
                )}
              </div>
            )}
          </div>
          <span className={clsx(
            'px-2 py-1 rounded-full text-xs font-medium ml-2',
            getStatusColor(raffle.status)
          )}>
            {getStatusText(raffle.status)}
          </span>
        </div>

        {/* Description */}
        {raffle.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            {raffle.description}
          </p>
        )}

      {/* Winner (if showWinner is true and raffle has winners) */}
      {showWinner && raffle.winners && raffle.winners.length > 0 && (
        <div className="mb-3 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="text-yellow-500" size={18} />
            <span className="text-sm font-bold text-gray-900 dark:text-white">Победители:</span>
          </div>
          {raffle.winners.slice(0, 3).map((winner) => (
            <div key={winner.place} className="flex items-center gap-2 text-sm mt-1">
              <span className="font-bold text-yellow-600 dark:text-yellow-400">
                {winner.place === 1 ? '🥇' : winner.place === 2 ? '🥈' : winner.place === 3 ? '🥉' : `${winner.place}.`}
              </span>
              <span className="text-gray-900 dark:text-white">
                {winner.first_name || winner.username || `User ${winner.user_id}`}
              </span>
              <span className="text-gray-500 dark:text-gray-400 text-xs">- {winner.prize_name}</span>
            </div>
          ))}
          {raffle.winners.length > 3 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              +{raffle.winners.length - 3} ещё...
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      {!showWinner && (
        <div className="mb-3">
          <div className="flex items-center gap-2 text-sm">
            <Users className="text-blue-500" size={16} />
            <div>
              <div className="text-xs text-gray-500">Билетов</div>
              <div className="font-bold text-gray-900 dark:text-white">
                {raffle.participants_count}
                {raffle.max_participants && `/${raffle.max_participants}`}
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Entry Price */}
        {!showWinner && (
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Цена билета
            </div>
            <div className="text-lg font-bold text-telegram-button">
              {raffle.entry_price.toFixed(0)} ₽
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            setModalImage(null);
          }}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={modalImage}
              alt="Full size"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setModalImage(null);
              }}
              className="absolute top-4 right-4 bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
